import React from 'react';
import { Button, Flex, LoadingOrbit, Text, ArrowClockwise } from '@chatsdet/ui';
import type { FailureDiagnostic } from '@chatsdet/types/issues';
import { DiagnosticReport } from './DiagnosticReport';

type DiagnosticsTabProps = {
  diagnosticStatus: string | null;
  diagnosticData: FailureDiagnostic | null;
  onRetry?: () => void;
  isRetrying?: boolean;
};

export function DiagnosticsTab({
  diagnosticStatus,
  diagnosticData,
  onRetry,
  isRetrying,
}: DiagnosticsTabProps) {
  if (!diagnosticStatus) {
    return (
      <div className="px-6 pb-6 pt-2">
        <Text size="sm" className="text-muted-foreground">
          No diagnostic analysis available.
        </Text>
      </div>
    );
  }

  if (diagnosticStatus === 'pending' || diagnosticStatus === 'running') {
    return (
      <div className="px-6 pb-6 pt-2">
        <Flex align="center" gap="sm" className="text-muted-foreground">
          <LoadingOrbit size="sm" />
          <Text size="sm">Analyzing failure...</Text>
        </Flex>
      </div>
    );
  }

  if (diagnosticStatus === 'failed') {
    return (
      <div className="px-6 pb-6 pt-2">
        <Flex align="center" gap="sm">
          <Text size="sm" className="text-muted-foreground">
            Diagnostic analysis was unable to complete.
          </Text>
          {onRetry ? (
            <Button variant="outline" size="sm" onPress={onRetry} isDisabled={isRetrying}>
              <Flex align="center" gap="xs">
                <ArrowClockwise className="w-3.5 h-3.5" />
                {isRetrying ? 'Retrying...' : 'Re-run Analysis'}
              </Flex>
            </Button>
          ) : null}
        </Flex>
      </div>
    );
  }

  if (diagnosticStatus !== 'completed' || !diagnosticData) {
    return (
      <div className="px-6 pb-6 pt-2">
        <Text size="sm" className="text-muted-foreground">
          No diagnostic analysis available.
        </Text>
      </div>
    );
  }

  return (
    <div className="px-6 pb-6 pt-2">
      <DiagnosticReport diagnostic={diagnosticData} />
    </div>
  );
}
